import { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import style from "../../Admin/admin.module.css";
import { Button, LabelBox } from "../../../components";
import { colors } from "../../../theme/colors";
import { CustomTable, StatusBadge } from "../../../components/DataTable";
import { CustomActionMenu } from "../../../components/Menu";
import {
  useGetUserRolesQuery,
  useGetUsersListQuery,
  useUpdateUserMutation,
} from "../../../services/api";
import {
  ConfirmationDialog,
  DetailsDialog,
  InputDialog,
} from "../../../components/Dialog";
import { Input } from "../../../components/UserInput";
import { updateStatusSchema } from "../../../yup";
import { getErrorMessage } from "../../../utils/common";
import {
  buildUserActions,
  renderCellText,
  resetDeactivateReasonField,
  useStatusDialogs,
} from "../userShared";
import { DEFAULT_HEALTH_ORG_ADMIN_ROLE_ID, DEFAULT_LABORATORY_ADMIN_ROLE_ID } from "../../../utils/constants";

/** Form values inferred from the update status schema. */
type FormState = typeof updateStatusSchema.__outputType;
/** Action identifiers for admin rows. */
type AdminAction = "view" | "edit" | "logs" | "deactivate" | "activate";

/** Props for the admin list tab. */
type AdminTabProps = {
  searchKey?: string;
  onClearSearch?: () => void;
  scope?: "healthOrg" | "laboratory";
};

/**
 * Formats an admin name for list rendering.
 *
 * Args:
 *   item: User row data from the API.
 *
 * Returns:
 *   JSX.Element: Rendered name text.
 */
const renderNameCell = (item: Users) =>
  renderCellText(
    `${item?.surname?.toUpperCase() ?? ""}, ${item?.given_name ?? ""} ${
      item?.prefix ?? ""
    }`,
    { className: style.cell }
  );

/**
 * Renders a status badge for list rows.
 *
 * Args:
 *   isActive: Status flag from the API.
 *
 * Returns:
 *   JSX.Element: Status badge component.
 */
const renderStatusBadge = (isActive?: boolean) => (
  <StatusBadge isActive={isActive ?? false} />
);

/**
 * Renders the admin list tab with search and status actions.
 *
 * Args:
 *   searchKey: Optional search query.
 *
 * Returns:
 *   JSX.Element: Admin list tab content.
 */
const AdminTab = ({ searchKey, onClearSearch, scope = "healthOrg" }: AdminTabProps) => {
  const { id } = useParams();
  const orgId = Number.isFinite(Number(id)) ? Number(id) : undefined;
  const isLaboratoryScope = scope === "laboratory";
  const healthOrgId = isLaboratoryScope ? undefined : orgId;
  const laboratoryId = isLaboratoryScope ? orgId : undefined;
  const navigate = useNavigate();
  const [updateUser] = useUpdateUserMutation();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [selectedAdmin, setSelectedAdmin] = useState<Users | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const {
    confirmOpen,
    reasonOpen,
    logsOpen,
    openConfirm,
    closeConfirm,
    openReason,
    closeReason,
    openLogs,
    closeLogs,
  } = useStatusDialogs();

  const { data: rolesData } = useGetUserRolesQuery({
    is_active: true,
    is_detailed: true,
  });

  const healthOrgAdminRoleId = useMemo(
    () =>
      rolesData?.items?.find(
        (role: { name: string }) => role?.name === "Health Organisation Admin"
      )?.id,
    [rolesData]
  );

  const laboratoryAdminRoleId = useMemo(
    () =>
      rolesData?.items?.find(
        (role: { name: string }) => role?.name === "Laboratory Admin"
      )?.id,
    [rolesData]
  );

  const [queryParam, setQueryParam] = useState({
    search_key: searchKey ?? "",
    order_by_columns: "surname",
    order: "Asc",
    is_detailed: true,
    health_org_id: healthOrgId,
    laboratory_id: laboratoryId,
    user_role_id: isLaboratoryScope
      ? laboratoryAdminRoleId ?? DEFAULT_LABORATORY_ADMIN_ROLE_ID
      : healthOrgAdminRoleId ?? DEFAULT_HEALTH_ORG_ADMIN_ROLE_ID,
  });

  const {
    data: adminsData,
    refetch: refetchAdmins,
    isFetching,
  } = useGetUsersListQuery(queryParam);

  useEffect(() => {
    if (!isLaboratoryScope && healthOrgAdminRoleId) {
      setQueryParam((prevParams) => ({
        ...prevParams,
        user_role_id: healthOrgAdminRoleId,
      }));
    }
    if (isLaboratoryScope && laboratoryAdminRoleId) {
      setQueryParam((prevParams) => ({
        ...prevParams,
        user_role_id: laboratoryAdminRoleId,
      }));
    }
  }, [healthOrgAdminRoleId, isLaboratoryScope, laboratoryAdminRoleId]);

  useEffect(() => {
    setQueryParam((prevParams) => ({
      ...prevParams,
      search_key: searchKey ?? "",
    }));
  }, [searchKey]);

  const handleMenu = useCallback(
    (value: AdminAction, admin: Users) => {
      setSelectedAdmin(admin);
      if (value === "deactivate" || value === "activate") {
        openConfirm();
      } else if (value === "view") {
        navigate(`/users/details/${admin?.id}`, {
          state: { userType: "ADMIN", source: "USER_DETAILS", healthOrgId },
        });
      } else if (value === "edit") {
        navigate(`/users/edit/${admin?.id}`, {
          state: { userType: "ADMIN" },
        });
      } else if (value === "logs") {
        openLogs();
      }
    },
    [healthOrgId, navigate, openConfirm, openLogs]
  );

  const fields = useMemo(
    () => [
      {
        id: "surname",
        label: "Name",
        flex: 2.5,
        sortable: true,
        type: "name",
        render: (item: Users) => renderNameCell(item),
      },
      {
        id: "mail",
        label: "Email Address",
        flex: 2.5,
        type: "name",
        render: (item: Users) =>
          renderCellText(item?.mail ?? "", { className: style.cell }),
      },
      {
        id: "is_active",
        label: "Status",
        type: "badge",
        flex: 1.3,
        sortable: true,
        render: (item: Users) => renderStatusBadge(item.is_active),
      },
      {
        id: "logo_s3_key_name",
        label: "Actions",
        type: "action",
        render: (item: Users) => (
          <CustomActionMenu
            item={item}
            isActive={item?.is_active ?? false}
            handleChange={handleMenu}
            actions={buildUserActions<AdminAction>([
              "view",
              ...(item.is_active ? (["edit"] as AdminAction[]) : []),
              "logs",
              item.is_active ? "deactivate" : "activate",
            ])}
          />
        ),
      },
    ],
    [handleMenu]
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<FormState>({
    resolver: yupResolver(updateStatusSchema),
  });

  /**
   * Updates admin activation status with an optional deactivation reason.
   *
   * Args:
   *   values: Validated form payload.
   *
   * Returns:
   *   Promise<void>: Resolves after status update and refetch.
   */
  const onSubmit = async (values: FormState) => {
    if (!selectedAdmin?.id) {
      setMessage({
        type: "error",
        text: "Please select a valid admin before updating status.",
      });
      return;
    }
    try {
      setIsLoading(true);
      const { deactivate_reason } = values;
      await updateUser({
        id: selectedAdmin.id,
        is_active: !selectedAdmin?.is_active,
        deactivate_reason: deactivate_reason ?? "",
      }).unwrap();
      setMessage({
        type: "success",
        text: "Status updated successfully.",
      });
      closeReason();
      resetDeactivateReasonField(setValue, clearErrors);
      await refetchAdmins();
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Something went wrong!"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {message && (
        <Box mt={2}>
          <LabelBox
            boxProps={{ height: 50, display: "flex", alignItems: "center" }}
            data={message}
            onClose={() => setMessage(null)}
          />
        </Box>
      )}
      <Box
        style={{
          maxHeight: "calc(100vh - 195px)",
          overflow: "auto",
          marginTop: "10px",
        }}
      >
        <CustomTable<Users>
          fields={fields}
          paginationRequired={true}
          data={adminsData?.items ?? []}
          loading={isFetching}
          onDoubleClick={(admin: Users) => {
            navigate(`/users/details/${admin?.id}`, {
              state: { userType: "ADMIN", healthOrgId },
            });
          }}
          defaultOrderBy={"surname"}
          searchTerm={searchKey}
          onClearSearch={onClearSearch}
        />
      </Box>
      {confirmOpen && selectedAdmin && (
        // Deactivation requires a reason; activation proceeds immediately.
        <ConfirmationDialog
          visible={confirmOpen}
          message={`Are you sure you want to ${
            selectedAdmin.is_active ? "deactivate " : "activate "
          }"${selectedAdmin.surname?.toUpperCase() ?? ""}, ${
            selectedAdmin.given_name ?? ""
          } ${selectedAdmin.prefix ?? ""}"?`}
          onClose={() => {
            closeConfirm();
          }}
          onConfirm={() => {
            closeConfirm();
            if (selectedAdmin?.is_active) {
              openReason();
            } else {
              onSubmit({ deactivate_reason: "" });
            }
          }}
        />
      )}
      {reasonOpen && (
        <InputDialog
          visible={reasonOpen}
          onClose={() => {
            closeReason();
            resetDeactivateReasonField(setValue, clearErrors);
          }}
        >
          <Box
            component="form"
            sx={{ marginX: "20px" }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Typography
              variant="h2"
              component="div"
              fontSize={16}
              fontWeight={700}
              color={colors.modalHeading}
              marginBottom={"10px"}
              sx={{ display: "flex", flexDirection: "row" }}
            >
              {"Enter the reason for deactivation"}
              <Typography
                color={"red"}
                fontSize={16}
                fontWeight={700}
                sx={{ marginLeft: "5px", marginTop: "-5px" }}
              >
                {"*"}
              </Typography>
            </Typography>
            <Input
              placeholder=""
              name="deactivate_reason"
              required
              register={register}
              multiline
              rows={3}
              errors={errors}
              sx={{
                width: "100%",
                margin: "1px",
                borderWidth: "1px",
              }}
            />
            <Box className={style.horizontalButtons} sx={{ paddingX: "50px" }}>
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  backgroundColor: colors.white,
                  ":hover": { backgroundColor: colors.white },
                }}
                variant="outlined"
                onClick={() => {
                  closeReason();
                  resetDeactivateReasonField(setValue, clearErrors);
                }}
                label={"Cancel"}
              />
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  ":hover": { backgroundColor: colors.primary },
                }}
                variant="contained"
                type="submit"
                label={"Submit"}
                loading={isLoading}
              />
            </Box>
          </Box>
        </InputDialog>
      )}
      {logsOpen && (
        <DetailsDialog
          visible={logsOpen}
          details={selectedAdmin}
          onClose={() => {
            closeLogs();
          }}
          onConfirm={() => {
            closeLogs();
          }}
        />
      )}
    </>
  );
};

export default AdminTab;
